import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Col, Radio, Row } from "antd";
import { chain, filter, keys, pluck, uniq } from "underscore";
import WidgetModuleLayout from "./WidgetModuleLayout";
import {
    AggregationField,
    AVAILABILITY_CHARTS,
    AvailabilityCharts,
    CommonWidgetMetrics,
    CrossChannelTableDataModules,
    IMyProductTrendsOriginalData,
    IPricingCompetitiveAnalysisOriginalData,
    IPricingCompetitiveAnalysisTrendsOriginalData,
    IProductAnalysisAvailabilityModule,
    MyProductTrendsModules,
    MyProductTrendsTypes,
    PricingCharts,
    ProductDeepdiveModules,
    TCrossChannelTableData,
} from "@ts/common-types";
import {
    chartToggleFiltersReducer,
    chartFiltersReducer,
    formatBbWinnersDistributionData,
    formatMyProductTrends,
} from "@ts/widget";
import WidgetCard from "@components/widgets/WidgetCard";
import {
    PRICING_CHART_TOGGLE_FILTERS_INTIAL_STATE,
    PRICING_CHART_FILTERS_INTIAL_STATE,
    AVAILABILITY_CHART_FILTERS_INTIAL_STATE,
    availabilityModuleRadioOptions,
} from "@ts/constants";
import { showToast } from "@ts/modals";
import { useCrossChannelTableData } from "@/hooks/AllCustomHooks";
import OtherSellersDataModalWidget from "../widgets/OtherSellersDataModalWidget";
import {
    BuyboxWinnersDistribution,
    PriceImpactBuyboxWinWidget,
    PricingAndDealCompetitionAnalysisWidget,
    SatisfactionDriversSummaryWidget,
    SummaryWidget,
    TrendsWithCompareModalWidget,
} from "../widgets";
import OutOfStockMarketShareWidget from "../widgets/OutOfStockMarketShareWidget";
import { includes } from "underscore";
import { formatAvailablityComparisonChartData } from "@/ts/widget";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";

const ProductAnalysisAvailabilityModule: React.FC<
    IProductAnalysisAvailabilityModule
> = ({
    loadingState,
    selectedSKU,
    availabilityData,
    channel,
    currencySymbol,
}) => {
    const [showAvailabilityTrends, setShowAvailabilityTrends] = useState(false);
    const [chartToggleFilters, setChartToggleFilters] = useReducer(
        chartToggleFiltersReducer,
        PRICING_CHART_TOGGLE_FILTERS_INTIAL_STATE
    );
    const [chartFilters, setChartFilters] = useReducer(
        chartFiltersReducer,
        AVAILABILITY_CHART_FILTERS_INTIAL_STATE
    );
    const [showOtherSellersDataModal, setShowOtherSellersDataModal] =
        useState(false);

    const onClickMoreHandler = (type: string) => {
        setShowOtherSellersDataModal(true);
    };

    const buyboxwinnerData = useMemo(() => {
        return formatBbWinnersDistributionData(
            availabilityData?.bbwinnerDistributionData
        );
    }, [availabilityData]);

    const channelName =
        CHANNEL_CONFIGS[channel].name.charAt(0).toUpperCase() +
        String(CHANNEL_CONFIGS[channel].name).slice(1);

    const myBrandTrendsCrossChannelData = useCrossChannelTableData(
        availabilityData?.myProductCrossChannelTableData,
        [],
        CrossChannelTableDataModules.AVAILABILITY
    );

    const myBrandTrendsCompetitionData = useCrossChannelTableData(
        availabilityData?.myProductCompetitionChannelTableData,
        [],
        CrossChannelTableDataModules.AVAILABILITY
    );

    const skuFilter = (
        dataToFilter:
            | IPricingCompetitiveAnalysisOriginalData
            | IPricingCompetitiveAnalysisTrendsOriginalData[],
        competitonSku: string[]
    ) =>
        filter(
            dataToFilter,
            (data) =>
                data.sku === selectedSKU || includes(competitonSku, data.sku)
        );

    const myBrandTrendsData = useMemo(() => {
        const competitonSku =
            chartFilters[PricingCharts.PRICE_DEALS_COMPETITION];

        const skusWithTitle =
            availabilityData?.myProductTrendsChartCompetitionChartData
                ? chain(
                      availabilityData?.myProductTrendsChartCompetitionChartData
                  )
                      .filter(({ sku }) => sku !== selectedSKU)
                      .uniq(CommonWidgetMetrics.SKU)
                      .map(({ sku, sku_title }) => ({ sku, sku_title }))
                      .value()
                : [];

        const generateSection = (
            data: Partial<IMyProductTrendsOriginalData>[],
            tableData: TCrossChannelTableData[],
            expandedTableData: TCrossChannelTableData[],
            groupBy: string | null = null
        ) => ({
            charts: {
                [MyProductTrendsTypes.AVAILABILITY]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.AVAILABILITY,
                    groupBy
                ),
                [MyProductTrendsTypes.BUYBOX]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.BUYBOX,
                    groupBy
                ),
            },
            table: { tableData, expandedTableData },
        });

        return {
            [MyProductTrendsModules.CROSS_CHANNEL]: {
                channels: uniq(
                    pluck(
                        availabilityData
                            ?.myProductTrendsChartCrossChannelChartData?.[
                            selectedSKU
                        ],
                        CommonWidgetMetrics.CHANNEL
                    )
                ),
                ...generateSection(
                    availabilityData
                        ?.myProductTrendsChartCrossChannelChartData?.[
                        selectedSKU
                    ],
                    myBrandTrendsCrossChannelData.tableData,
                    myBrandTrendsCrossChannelData.expandedTableData,
                    CommonWidgetMetrics.CHANNEL
                ),
            },
            [MyProductTrendsModules.COMPETITION]: {
                skus: skusWithTitle,
                ...generateSection(
                    skuFilter(
                        availabilityData?.myProductTrendsChartCompetitionChartData,
                        competitonSku
                    ),
                    myBrandTrendsCompetitionData.tableData,
                    myBrandTrendsCompetitionData.expandedTableData,
                    CommonWidgetMetrics.SKU
                ),
            },
            [MyProductTrendsModules.COMPARISON]: {
                skus: [
                    AggregationField.ROAS,
                    AggregationField.SALE,
                    AggregationField.SPEND,
                    AggregationField.TOTAL_SALES,
                ],
                ...generateSection(
                    availabilityData?.myProductComparisonChartData,
                    myBrandTrendsCrossChannelData.tableData,
                    myBrandTrendsCrossChannelData.expandedTableData,
                    CommonWidgetMetrics.SKU
                ),
            },
        };
    }, [
        availabilityData,
        chartFilters[PricingCharts.PRICE_DEALS],
        chartFilters[PricingCharts.PRICE_DEALS_COMPETITION],
        myBrandTrendsCrossChannelData,
        myBrandTrendsCompetitionData,
    ]);

    const formattedMybrandsTrendsData = useMemo(() => {
        const availabilityRawData =
            availabilityData?.myProductComparisonChartData || [];

        const formattedChartData =
            formatAvailablityComparisonChartData(availabilityRawData);

        const existingSeries =
            myBrandTrendsData[MyProductTrendsModules.COMPARISON]?.charts
                ?.availability?.chartDataSeries?.series || [];

        return {
            ...myBrandTrendsData,
            [MyProductTrendsModules.COMPARISON]: {
                ...myBrandTrendsData[MyProductTrendsModules.COMPARISON],
                charts: {
                    ...myBrandTrendsData[MyProductTrendsModules.COMPARISON]
                        .charts,
                    availability: {
                        ...myBrandTrendsData[MyProductTrendsModules.COMPARISON]
                            .charts.availability,
                        chartDataSeries: {
                            ...myBrandTrendsData[
                                MyProductTrendsModules.COMPARISON
                            ].charts.availability.chartDataSeries,
                            series: [
                                ...existingSeries,
                                ...formattedChartData.series,
                            ],
                        },
                    },
                    buybox: {
                        ...myBrandTrendsData[MyProductTrendsModules.COMPARISON]
                            .charts.buybox,
                        chartDataSeries: {
                            ...myBrandTrendsData[
                                MyProductTrendsModules.COMPARISON
                            ].charts.buybox.chartDataSeries,
                            series: [
                                ...existingSeries,
                                ...formattedChartData.series,
                            ],
                        },
                    },
                },
            },
        };
    }, [myBrandTrendsData]);

    return (
        <WidgetModuleLayout
            loaderCondition={
                keys(availabilityData).length === 0 ||
                loadingState?.[ProductDeepdiveModules.AVAILABILITY]
            }
        >
            <SummaryWidget
                widgetData={
                    availabilityData?.availabilityBuyboxEstimatedLossData
                }
                activeTab={ProductDeepdiveModules.AVAILABILITY}
                onClickMoreHandler={onClickMoreHandler}
            />
            <SatisfactionDriversSummaryWidget
                moduleType={ProductDeepdiveModules.AVAILABILITY}
                widgetData={{
                    ...availabilityData.workingNotWorkingInsights,
                    aiSummary: availabilityData.aiSummary,
                }}
            />
            <TrendsWithCompareModalWidget
                title="Availability Trends Over Time"
                widgetLinkName="Compare"
                widgetData={availabilityData?.availabilityTrendsData}
                graphAttributes={{
                    yAxisTitle: `Availablity (%)`,
                    yAxisOppositeTitle: "Buybox",
                    yAxisOppositeDashStyleReq: true,
                    yAxisOppositeDashLineColor: "green",
                }}
                onWidgetLinkHandler={() => setShowAvailabilityTrends(true)}
                compare={true}
                maxDates={5}
                chart={AVAILABILITY_CHARTS.AVAILABILITY_TRENDS_OVERTIME}
            />
            <Row className="my-4" justify="center" align="middle" gutter={16}>
                <Col span={12}>
                    <BuyboxWinnersDistribution
                        widgetData={buyboxwinnerData}
                        currencySymbol={currencySymbol}
                        tooltipFormatter={(point) =>
                            `<div>Avg Price: ${point.value}</div>`
                        }
                    />
                </Col>
                <Col span={12}>
                    <PriceImpactBuyboxWinWidget
                        widgetData={availabilityData.priceImpactOnBuyBoxWinData}
                        {...{ currencySymbol, chartFilters, setChartFilters }}
                        maxDates={5}
                    />
                </Col>
            </Row>
            <TrendsWithCompareModalWidget
                title="My Product VS Competition"
                widgetLinkName="Compare"
                widgetData={availabilityData?.productVsCompetitionTrendsData}
                graphAttributes={{
                    yAxisTitle: "Availablity %",
                    xAxisTitle: "",
                    hideXAxisTitle: true,
                }}
                compare={false}
                selectedSku={selectedSKU}
                maxSelectableSkus={5}
                chart={AVAILABILITY_CHARTS.MY_PRODUCT_VS_COMPETITION}
            />
            <OutOfStockMarketShareWidget
                widgetData={availabilityData?.OosMsDynamicsChartData}
                maxDate={5}
            />
            <OtherSellersDataModalWidget
                title={`${availabilityData?.otherSellersData.length} Other Sellers on ${channelName}`}
                show={showOtherSellersDataModal}
                setShow={setShowOtherSellersDataModal}
                widgetData={availabilityData?.otherSellersData}
            />
            {showAvailabilityTrends && (
                <PricingAndDealCompetitionAnalysisWidget
                    title={`My Brands Trends on ${CHANNEL_CONFIGS[channel].name}`}
                    widgetData={formattedMybrandsTrendsData}
                    show={showAvailabilityTrends}
                    setShow={setShowAvailabilityTrends}
                    radioOptions={availabilityModuleRadioOptions}
                    {...{ chartFilters, setChartFilters, currencySymbol }}
                    isComparisonRequired={true}
                />
            )}
        </WidgetModuleLayout>
    );
};

export default ProductAnalysisAvailabilityModule;
